/*    
<summary>
   This class component is all about Managing Treatment data functionality.
   Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable, toJS } from "mobx";
import moment from "moment";
import toast from "react-hot-toast";
import { emptyValue } from "../../constants/constants";
import {
  registrationTypeOptions
} from "../../constants/options.constant";
import URLConstants from "../../constants/url-constants";
import config from "../../helpers/config-helper";
import { getBusinessTypeIdFromLocalStorage, getLanguageFromLocalStorage, getStoreIdFromLocalStorage, getUserTypeFromLocalStorage } from "../../helpers/localstorage-helper";
import { IAddTreatmentManagement } from "../../models/forms/IAddTreatment";
import { IObservableInitialState, IOption, IOptionTreatment } from "../../models/ICommon";
import {
  ITreatmentFilter,
  ITreatmentInitialFilterState,
  ITreatmentStoreFilter,
} from "../../models/IFilter";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import {
  ITreatmentManagementList,
  ITreatmentManagementListVM,
  ITreatmentManagementVM,
} from "../../models/response/ITreatmentManagementResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { formatMessage } from "../../translations/format-message";
import { initialState } from "../initialState/add-edit-treatment-state";
import { initialState as GetAllTreatmentManagementState } from "../initialState/get-all-treatment-state";
import { treatmentInitialFilterState, treatmentInitialStoreFilterState } from "../initialState/initial-filter-state";
import * as baseService from "../service/base-service";
import { AuthStore } from "./auth-store";
import userTypeEnum from "../../constants/enums/user-type-enum";

const appConfig = config();

const dateFormat = appConfig.REACT_APP_DATE_FORMAT;
const authStore = new AuthStore();

export class TreatmentManagementStore implements ICommonState {
  inProgress = false;
  error = "";
  treatmentList: ITreatmentManagementListVM = GetAllTreatmentManagementState;

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  addUpdateTreatmentManagementState = { ...this.initialStateValue };
  deleteTreatmentManagementState = { ...this.initialStateValue };

  activeInactiveState = { ...this.initialStateValue };

  treatment: ITreatmentManagementVM | undefined = undefined;
  treatmentStore: ITreatmentManagementVM[] | undefined = undefined;
  treatmentState = { ...this.initialStateValue };
  addUpdateStoreTreatmentOrderState = { ...this.initialStateValue };
  treatmentFilter: ITreatmentFilter = { ...treatmentInitialFilterState };
  treatmentStoreFilter: ITreatmentStoreFilter = { ...treatmentInitialStoreFilterState };

  getTreatmentDataAccordionState: IObservableInitialState = {
    ...this.initialStateValue
  };

  getTreatmentSummaryState: IObservableInitialState = {
    ...this.initialStateValue
  };

  treatmentSummaryData: IOptionTreatment[] = [];

  treatmentAccordionData: Map<number, any[]> = new Map();


  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      treatmentList: observable,
      treatment: observable,
      addUpdateTreatmentManagementState: observable,
      deleteTreatmentManagementState: observable,
      getTreatmentDataAccordionState: observable,
      getTreatmentSummaryState: observable,
      treatmentSummaryData: observable,
      treatmentStore: observable,
      treatmentState: observable,
      treatmentFilter: observable,
      addUpdateStoreTreatmentOrderState: observable,
      treatmentAccordionData: observable,
      treatmentStoreFilter: observable,
      GetTreatmentManagementListService: action,
      GetSearchTreatmentRegistrationFilterService: action,
      AddTreatmentManagementService: action,
      UpdateTreatmentManagementService: action,
      GetTreatmentListSummaryService: action,
      UpdateTreatmentManagementStatusService: action,
      DeleteTreatmentManagementService: action,
      GetTreatmentManagementByIdService: action,
      GetTreatmentListByCatIdService: action,
      GetTreatmentListBySearchService: action,
      UpdateStoreTreatmentOrderIdService: action,
      setTreatmentStoreFilterDetail: action,
      resettreatmentStoreFilterDetail: action,
      clearTreatmentData: action,
      reset: action,
      resetTreatment: action,
      resetTreatmentManagementDetail: action,
      resetAddUpdateStoreTreatmentOrderState: action,
      resetAddUpdateTreatmentManagementState: action,
      setTreatmentFilterDetail: action,
      resettreatmentFilterDetail: action,
      allTreatmentManagement: computed,
      treatmentDetail: computed,
      getTreatmentFilter: computed,
      getTreatmentData: computed,
      getTreatmentSummary: computed,
      getTreatmentStoreFilter: computed,
    });
  }

  /**
   * This function is used to Get All StaffManagements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetTreatmentManagementListService = (
    currentPage: number,
    pagerSize: number,
    orderBy: string,
    column: string,
    filter: ITreatmentInitialFilterState,
    allChildIds: number[]
  ) => {
    this.inProgress = true;
    const categoryQuery =
      filter.CategoryId !== -1 && filter.BusinessTypeId !== -1
        ? [filter.CategoryId, ...allChildIds]
            .map(id => `&category=${id}`)
            .join("")
        : "";
    const url = `${URLConstants.GetTreatmentManagementFilter
      }?PageNo=${currentPage}&PageSize=${pagerSize}&treatmentName=${filter.TreatmentName
      }&OrderBy=${orderBy}&Column=${column}&is_invalid=${filter.IsInvalid}${filter.BusinessTypeId !== -1
        ? `&businessType=${filter.BusinessTypeId}`
        : ""
      }${categoryQuery}`;

    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<
            IApiSuccessResponse<ITreatmentManagementListVM>
          >
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.treatmentList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };


  UpdateStoreTreatmentOrderIdService = (data: any, storeId: number) => {
    this.addUpdateStoreTreatmentOrderState.inProgress = true;
    let url = URLConstants.UpdateStoreTreatmentOrderId;
    let mappedData = data.map((item: any) => {
      return {
        Id: item.TreatmentDetailsId,
        OrderId: item.OrderId,
        StoreId: storeId,
        CategoryId: item.CategoryId
      };
    })
    return baseService
      .putRequest(url, { treatmentDetails: mappedData })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateStoreTreatmentOrderState.error = response.data.Message;
        } else { this.addUpdateStoreTreatmentOrderState.success = true };
      })
      .catch((err: string) => {
        this.addUpdateStoreTreatmentOrderState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStoreTreatmentOrderState.inProgress = false;
        })
      );
  };

  resetAddUpdateStoreTreatmentOrderState = () => {
    this.addUpdateStoreTreatmentOrderState = { ...this.initialStateValue };
  };

  /**
   * This function is used to fetch the list of treatment registrations based on the provided search query and pagination details.
   * @param currentPage : Current Page Number (used for pagination)
   * @param pagerSize : Number of items per page (used for pagination)
   * @param searchQuery : Search query string that filters treatment registrations
   * @returns
   */
  GetSearchTreatmentRegistrationFilterService = (
    currentPage: number,
    pagerSize: number,
    searchQuery: string,
    businessTypeId?: number // optional, since backend expects int?
  ) => {
    this.inProgress = true;
  
    const params = new URLSearchParams({
      PageNo: currentPage.toString(),
      PageSize: pagerSize.toString(),
      Search: searchQuery || "", // optional value
    });
  
    // Append businessTypeId only if it's defined
    if (businessTypeId !== undefined && businessTypeId !== null) {
      params.append("businessTypeId", businessTypeId.toString());
    }
  
    const url = `${URLConstants.GetSearchTreatmentRegistrationFilter}?${params.toString()}`;
  
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<
            IApiSuccessResponse<ITreatmentManagementListVM>
          >
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.treatmentList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };
  


  get getAllTreatmentOptionsAll(): IOption[] {
    const options: IOption[] = [];

    if (this.treatmentList && this.treatmentList.TreatmentVM?.length > 0) {
      this.treatmentList.TreatmentVM.map((treatment: ITreatmentManagementVM) => {
        options.push({
          id: treatment.Id,
          value: treatment.TreatmentName,
          disabled: false,
        });
      });
    }

    return options;
  }

  GetTreatmentListByCatIdService = (id: number, storeId: number) => {
    this.getTreatmentDataAccordionState.inProgress = true;
    const BusinessTypeId = getBusinessTypeIdFromLocalStorage();
    const urlParams = new URLSearchParams();
    urlParams.append('StoreId', String(storeId));
    urlParams.append('CategoryId', String(id));
    if (BusinessTypeId > 0) {
      urlParams.append('businessTypeId', String(BusinessTypeId));
    }
    let url = `${URLConstants.GetTreatmentRegistartionStoreId}/CategoryId?` + urlParams.toString();
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error && !Array.isArray(response.data.Data)) {
          this.getTreatmentDataAccordionState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          if (Array.isArray(data)) {
            let mappedData = data.map((item: any) => {
              return {
                ...item.treatments,
                CategoryId: item.CategoryId,
                OrderId: item.OrderId,
                Id: item.treatments.Id,
                TreatmentDetailsId: item.Id,
              };
              // return {
              //   "Id": 1,
              //   "RegistrationType": 0,
              //   "BusinessTypeId": 1,
              //   "CategoryId": 1,
              //   "Time": 1,
              //   "TreatmentName": "lkjh",
              //   "FeeExcludingTax": 33,
              //   "FeeIncludingTax": 35,
              //   "TaxAmount": 2,
              //   "TaxId": 1,
              //   "Status": 0,
              //   "Note": "",
              //   "OptionMenu": 0,
              //   "Description": "qwertyuiopsdfghjklzxcvbnm",
              //   "optionVm": []
              // }
            })
            // mappedData.sort((a, b) => a.OrderId - b.OrderId);
            if (this.treatmentAccordionData.has(id)) {
              // Replace existing data
              this.treatmentAccordionData.set(id, mappedData);
            } else {
              // Append new entry
              this.treatmentAccordionData.set(id, [
                ...(this.treatmentAccordionData.get(id) || []),
                ...mappedData,
              ]);
            }
          }
        }
      })
      .catch((err: string) => {
        this.getTreatmentDataAccordionState.error = err;
      })
      .finally(
        action(() => {
          this.getTreatmentDataAccordionState.inProgress = false;
        })
      );
  };

  GetTreatmentListBySearchService = (search: string) => {
    this.getTreatmentDataAccordionState.inProgress = true;
    let storeId = getStoreIdFromLocalStorage();
    const BusinessTypeId = getBusinessTypeIdFromLocalStorage();
    const urlParams = new URLSearchParams();
    urlParams.append('StoreId', String(storeId));
    urlParams.append('Search', String(search));
    if (BusinessTypeId > 0) {
      urlParams.append('businessTypeId', String(BusinessTypeId));
    }
    let url = `${URLConstants.GetTreatmentRegistartionStoreId}/Search?` + urlParams.toString();
    this.treatmentAccordionData.clear();
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error && !Array.isArray(response.data.Data)) {
          this.getTreatmentDataAccordionState.error = response.data.Message;
          this.treatmentAccordionData.clear();
        } else {
          let data = response.data.Data;
          this.treatmentAccordionData.clear();
          if (Array.isArray(data) && data.length > 0) {
            // data.sort((a, b) => a.OrderId - b.OrderId);
            data.forEach((item: any) => {
              let mappedData = {
                ...item.treatments,
                CategoryId: item.CategoryId,
                OrderId: item.OrderId,
                Id: item.treatments.Id,
                TreatmentDetailsId: item.Id,
              };
              if (this.treatmentAccordionData.has(item.CategoryId)) {
                let prevData = this.treatmentAccordionData.get(item.CategoryId) || [];
                this.treatmentAccordionData.set(item.CategoryId, [...prevData, { ...mappedData }]);
              } else {
                this.treatmentAccordionData.set(item.CategoryId, [
                  { ...mappedData },
                ]);
              }
            })
          }
        }
      })
      .catch((err: string) => {
        this.getTreatmentDataAccordionState.error = err;
        this.treatmentAccordionData.clear();
      })
      .finally(
        action(() => {
          this.getTreatmentDataAccordionState.inProgress = false;
        })
      );
  };

  get getTreatmentData() {
    return this.treatmentAccordionData
  };

  clearTreatmentData = () => {
    this.treatmentAccordionData.clear();
  };


  /**
   * This function is used to map staffList to allStaffManagement List suitable for Grid Component.
   * @returns allStaffManagementsList
   */
  get allTreatmentManagement(): ITreatmentManagementList[] {
    if (
      this.treatmentList?.TreatmentVM &&
      this.treatmentList.TreatmentVM?.length > 0
    ) {
      return this.treatmentList.TreatmentVM.map((treatment) => {
        let language = getLanguageFromLocalStorage();
        let isDelete = true;
        let isEdit = true;

        let registrationArray: IOption[] = registrationTypeOptions.filter(
          (option: IOption) => option.id === treatment.RegistrationType
        );

        return {
          Id: treatment.Id,
          language: language,
          RegistrationType:
            registrationArray.length > 0
              ? registrationArray[0].id > 0 ? treatment.StoreName ?? emptyValue : treatment.HeadquarterName ?? emptyValue
              : emptyValue,
          BusinessTypeId: treatment.BusinessTypeId,
          Category:
            treatment?.Category?.length > 0
              ? treatment.Category.map((obj: any) => obj.CategoryName).join(
                " > "
              )
              : "",
          IsReservationSite: treatment.IsReservationSite,
          TreatmentName: treatment.TreatmentName,
          Time: treatment.Time,
          TimeValue: treatment.TimeValue,
          FeeIncludingTax: treatment.FeeIncludingTax,
          TaxId: treatment.TaxId,
          TaxValue: treatment.TaxValue,
          Status: treatment.Status,
          StartDate: treatment.StartDate,
          EndDate: treatment.EndDate,
          Note: treatment.Note,
          OptionMenu: treatment.OptionMenu,
          TaxAmount: treatment.TaxAmount,
          FeeExcludingTax: treatment.FeeExcludingTax,
          Description: treatment.Description,
          isDelete: isDelete,
          isEdit: isEdit,
        };
      });
    }
    return [];
  }

  /**
   * This function is used to Get StaffManagement Details by Id by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  GetTreatmentManagementByIdService = (id: number, storeId?: number) => {
    this.treatmentState.inProgress = true;
    let url = storeId ? `${URLConstants.GetTreatmentById}/${id}?storeId=${storeId}` : `${URLConstants.GetTreatmentById}/${id}`;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<ITreatmentManagementVM>>
        ) => {
          if (response.data.Error) {
            this.treatmentState.error = response.data.Message;
          } else {
            let data = response.data.Data;
            this.treatment = data;
          }
        }
      )
      .catch((err: string) => {
        this.treatmentState.error = err;
      })
      .finally(
        action(() => {
          this.treatmentState.inProgress = false;
        })
      );
  };

  /**
   * This function provides initail values to the Add StaffManagement Form.
   * @returns StaffManagement Details
   *
   */
  get treatmentDetail(): IAddTreatmentManagement {
    if (this.treatment) {
      return {
        RegistrationType: this.treatment.RegistrationType || 0,
        BusinessTypeId: this.treatment.BusinessTypeId ?? -1,
        CategoryId: this.treatment.CategoryId ? this.treatment.CategoryId : -1,
        CategoryPath: "",
        IsReservationSite: Boolean(this.treatment.IsReservationSite),
        TreatmentName: this.treatment.TreatmentName ?? "",
        Time: this.treatment.Time ?? -1,
        TimeValue: this.treatment.TimeValue ?? 0,
        FeeIncludingTax: this.treatment.FeeIncludingTax ?? 0,
        FeeExcludingTax: this.treatment.FeeExcludingTax ?? 0,
        TaxId: this.treatment.TaxId ?? 0,
        TaxValue: this.treatment.TaxValue ?? 0,
        TaxAmount: this.treatment.TaxAmount ?? "",
        StartDate:
          this.treatment.StartDate == "0001-01-01T00:00:00.0000000Z"
            ? ""
            : (this.treatment.StartDate ? moment(this.treatment.StartDate).format(dateFormat) : ""),
        EndDate:
          this.treatment.EndDate == "0001-01-01T00:00:00.0000000Z"
            ? ""
            : (this.treatment.EndDate ? moment(this.treatment.EndDate).format(dateFormat) : ""),
        Status: this.treatment.Status ? this.treatment.Status.toString() : "0",
        Note: this.treatment.Note ?? "",
        OptionMenu: (this.treatment.OptionMenu !== null || this.treatment.OptionMenu !== undefined) ? this.treatment.OptionMenu?.toString() : "1",
        Description: this.treatment.Description,
        StoreId: this.treatment.StoreId ?? null,
        StaffOptions: this.treatment.TreatmentStaff ? this.treatment.TreatmentStaff.map((staff: any) => (staff.StaffId.toString())) : [],
        Image: this.treatment.TreatmentDetails?.Image ?? "",
        IsFeatured: this.treatment.TreatmentDetails?.IsFeatured ?? false,
        StoreName: this.treatment.StoreName ?? "",
        HeadquarterName: this.treatment.HeadquarterName ?? "",
        RegistrationTypeName: this.treatment.RegistrationType && this.treatment.RegistrationType > 0 ? this.treatment.StoreName ?? "" : this.treatment.HeadquarterName ?? "",
        IsAutoCalculateTax: false,
      };
    }
    return {
      ...initialState, BusinessTypeId: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter] || getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store]
        ? getBusinessTypeIdFromLocalStorage() : -1,
      RegistrationType: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter] ? 0 : getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store] ? 1 : initialState.RegistrationType,
    };
  }

  GetTreatmentListSummaryService = (userType: number, businessType: number, storeId?: number) => {
    this.getTreatmentSummaryState.inProgress = true;
    let url = storeId && storeId > 0 ? `${URLConstants.GetTreatmentSummary}?userType=${userType}&storeId=${storeId}&businessType=${businessType}` : `${URLConstants.GetTreatmentSummary}?userType=${userType}&businessType=${businessType}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getTreatmentSummaryState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.treatmentSummaryData = { ...data };

        }
      })
      .catch((err: string) => {
        this.getTreatmentSummaryState.error = err;
      })
      .finally(
        action(() => {
          this.getTreatmentSummaryState.inProgress = false;
        })
      );
  };

  get getTreatmentSummary() {
    const options: IOption[] = [];
    const plainObject = toJS(this.treatmentSummaryData);
    const treatmentsArray = Object.values(plainObject);
    if (treatmentsArray && treatmentsArray.length > 0) {
      treatmentsArray.forEach((treatment: any) => {
        options.push({
          id: treatment.TreatmentId,
          value: treatment.TreatmentName,
          disabled: false,
        });
      });
    }

    return options;
  }

  /*
    This function is used to reset all staff observables to their initial values.  
    */
  resetTreatmentManagementDetail = () => {
    this.treatment = undefined;
    this.treatmentState = { ...this.initialStateValue };
    this.treatmentStore = undefined;
    this.treatmentSummaryData = [];
    this.getTreatmentSummaryState = { ...this.initialStateValue }
  };

  /**
   * This function is used to Add New StaffManagement by calling an API.
   * @param data : StaffManagement Data
   * @returns
   */
  AddTreatmentManagementService = (data: IAddTreatmentManagement) => {
    this.addUpdateTreatmentManagementState.inProgress = true;
    let url = URLConstants.AddTreatment;
    const requestData = data.StaffOptions && data.StaffOptions.length > 0 && getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store] ? {
      ...data,
      staffMappingDetails: data.StaffOptions?.map((staff) => { return { StaffId: Number(staff), StoreId: data.StoreId } }),
      RegistrationType: data.RegistrationType?.toString(),
      CategoryId: data.CategoryId,
      IsFeatured: data.IsFeatured ? data.IsFeatured : false,
      StoreId: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter] ? null : data.StoreId, //add null as for headquarter login
    } : {
      ...data,
      RegistrationType: data.RegistrationType?.toString(),
      CategoryId: data.CategoryId,
      StoreId: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter] ? null : data.StoreId, //add null as for headquarter login
    };
    return baseService
      .postRequest(url, requestData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateTreatmentManagementState.error = response.data.Message;
        } else {
          this.addUpdateTreatmentManagementState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateTreatmentManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateTreatmentManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Updating Existing StaffManagement Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param data
   * @returns
   */
  UpdateTreatmentManagementService = (
    id: number,
    data: IAddTreatmentManagement
  ) => {
    this.addUpdateTreatmentManagementState.inProgress = true;
    let url = `${URLConstants.UpdateTreatment}`;

    const requestData = data.StaffOptions && data.StaffOptions.length > 0 && getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store] ? {
      Id: id,
      ...data,
      UpdateStaffMappingDetails: data.StaffOptions?.map((staff) => { return { StaffId: Number(staff), StoreId: data.StoreId } }),
      RegistrationType: data.RegistrationType?.toString(),
      CategoryId: data.CategoryId?.toString(),
      StoreId: data.StoreId ? data.StoreId : null, //add null as for headquarter login
    } : {
      Id: id,
      ...data,
      RegistrationType: data.RegistrationType?.toString(),
      CategoryId: data.CategoryId?.toString(),
      StoreId: data.StoreId ? data.StoreId : null
    };


    return baseService
      .putRequest(url, requestData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateTreatmentManagementState.error = response.data.Message;
        } else {
          this.addUpdateTreatmentManagementState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateTreatmentManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateTreatmentManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetAddUpdateTreatmentManagementState = () => {
    this.addUpdateTreatmentManagementState.inProgress = false;
    this.addUpdateTreatmentManagementState.success = false;
    this.addUpdateTreatmentManagementState.error = "";
  };

  /**
   * This function is used to Delete StaffManagement by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  DeleteTreatmentManagementService = (id: number) => {
    this.deleteTreatmentManagementState.inProgress = true;
    let url = `${URLConstants.DeleteTreatment}/${id}`; // Correct URL construction for DELETE operation

    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteTreatmentManagementState.error = response.data.Message;
        } else {
          this.deleteTreatmentManagementState.success = true;
        }
      })
      .catch((err: string) => {
        this.deleteTreatmentManagementState.error = err;
      })
      .finally(
        action(() => {
          this.deleteTreatmentManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetDeleteTreatmentManagement = () => {
    this.deleteTreatmentManagementState.inProgress = false;
    this.deleteTreatmentManagementState.success = false;
    this.deleteTreatmentManagementState.error = "";
  };

  /**
   * This function is used to Updating Existing StaffManagement state (i.e. active/inactive) Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param state: StaffManagement state
   * @returns
   */
  UpdateTreatmentManagementStatusService = (id: number, state: boolean) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.TreatmentManagementStatus;
    let treatmentData: any = {
      Id: id,
      IsActive: state,
    };
    return baseService
      .putRequest(url, treatmentData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };

  /*
        This function is used to set the filter details to the Filter observable.  
        */
  setTreatmentFilterDetail = (filter: ITreatmentFilter) => {
    this.treatmentFilter = { ...filter };
  };

  resettreatmentFilterDetail = () => {
    this.treatmentFilter = { ...treatmentInitialFilterState };
  };

  get getTreatmentFilter(): ITreatmentFilter {
    return this.treatmentFilter;
  }


  setTreatmentStoreFilterDetail = (filter: ITreatmentStoreFilter) => {
    this.treatmentStoreFilter = { ...filter };
  };

  resettreatmentStoreFilterDetail = () => {
    this.treatmentStoreFilter = { ...treatmentInitialStoreFilterState };
  };

  get getTreatmentStoreFilter(): ITreatmentStoreFilter {
    return this.treatmentStoreFilter;
  }

  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateTreatmentManagementState.success = false;
    this.addUpdateTreatmentManagementState.error = "";
    this.deleteTreatmentManagementState = { ...this.initialStateValue };
    this.addUpdateTreatmentManagementState.inProgress = false;
    this.treatmentState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all staff observables to their initial values.
   * @returns
   */
  resetTreatment = () => {
    this.error = "";
    this.inProgress = false;
    this.treatmentList = GetAllTreatmentManagementState;

    this.addUpdateTreatmentManagementState = { ...this.initialStateValue };
    this.deleteTreatmentManagementState = { ...this.initialStateValue };
    this.treatment = undefined;
    this.treatmentState = { ...this.initialStateValue };
  };
}

export default new TreatmentManagementStore();


